import React from 'react';
import { useTranslation } from 'react-i18next';
// Saxophone fingering data — standard fingerings for written pitch
const FINGERING_CHART = {
  'Bb3': { octave: false, lh1: true, lh2: true, lh3: true, rh1: true, rh2: true, rh3: true, lowBb: true },
  'B3':  { octave: false, lh1: true, lh2: true, lh3: true, rh1: true, rh2: true, rh3: true, lowB: true },
  'C4':  { octave: false, lh1: true, lh2: true, lh3: true, rh1: true, rh2: true, rh3: true },
  'C#4': { octave: false, lh1: true, lh2: true, lh3: true, rh1: true, rh2: true, rh3: true, lowCSharp: true },
  'D4':  { octave: false, lh1: true, lh2: true, lh3: true, rh1: true, rh2: true, rh3: true },
  'Eb4': { octave: false, lh1: true, lh2: true, lh3: true, rh1: true, rh2: true, lowEb: true },
  'E4':  { octave: false, lh1: true, lh2: true, lh3: true, rh1: true, rh2: true },
  'F4':  { octave: false, lh1: true, lh2: true, lh3: true, rh1: true },
  'F#4': { octave: false, lh1: true, lh2: true, lh3: true, rh2: true },
  'G4':  { octave: false, lh1: true, lh2: true, lh3: true },
  'G#4': { octave: false, lh1: true, lh2: true, lh3: true, gSharp: true },
  'A4':  { octave: false, lh1: true, lh2: true },
  'Bb4': { octave: false, lh1: true, bis: true },
  'B4':  { octave: false, lh1: true },
  'C5':  { octave: false, lh2: true },
  'C#5': {},
  'D5':  { octave: true, lh1: true, lh2: true, lh3: true, rh1: true, rh2: true, rh3: true },
  'Eb5': { octave: true, lh1: true, lh2: true, lh3: true, rh1: true, rh2: true, lowEb: true },
  'E5':  { octave: true, lh1: true, lh2: true, lh3: true, rh1: true, rh2: true },
  'F5':  { octave: true, lh1: true, lh2: true, lh3: true, rh1: true },
  'F#5': { octave: true, lh1: true, lh2: true, lh3: true, rh2: true },
  'G5':  { octave: true, lh1: true, lh2: true, lh3: true },
  'G#5': { octave: true, lh1: true, lh2: true, lh3: true, gSharp: true },
  'A5':  { octave: true, lh1: true, lh2: true },
  'Bb5': { octave: true, lh1: true, bis: true },
  'B5':  { octave: true, lh1: true },
  'C6':  { octave: true, lh2: true },
  'C#6': { octave: true },
  'D6':  { octave: true, palmD: true },
  'Eb6': { octave: true, palmD: true, palmEb: true },
  'E6':  { octave: true, palmD: true, palmEb: true, sideE: true },
  'F6':  { octave: true, palmD: true, palmEb: true, palmF: true },
  'F#6': { octave: true, palmD: true, palmEb: true, palmF: true, sideE: true },
};

function SaxFingeringDiagram({ noteName }) {
  const { t } = useTranslation();
  const fingering = FINGERING_CHART[noteName] || {};

  const keyColor = (active) => active ? '#FFD700' : '#2a2a2a';
  const keyStroke = (active) => active ? '#FFA500' : '#555';

  return (
    <div className="sax-fingering">
      <div className="sax-fingering-label">{noteName || '—'}</div>
      <svg viewBox="0 0 120 360" className="sax-fingering-svg">
        {/* Octave key */}
        <ellipse cx="35" cy="20" rx="18" ry="8"
          fill={keyColor(fingering.octave)} stroke={keyStroke(fingering.octave)} strokeWidth="2" />
        <text x="35" y="24" textAnchor="middle" fontSize="8" fill="#ccc">Oct</text>

        {/* Palm keys (top right) */}
        <rect x="75" y="10" width="30" height="12" rx="4"
          fill={keyColor(fingering.palmF)} stroke={keyStroke(fingering.palmF)} strokeWidth="1.5" />
        <text x="90" y="19" textAnchor="middle" fontSize="7" fill="#ccc">F</text>

        <rect x="75" y="28" width="30" height="12" rx="4"
          fill={keyColor(fingering.palmEb)} stroke={keyStroke(fingering.palmEb)} strokeWidth="1.5" />
        <text x="90" y="37" textAnchor="middle" fontSize="7" fill="#ccc">Eb</text>

        <rect x="75" y="46" width="30" height="12" rx="4"
          fill={keyColor(fingering.palmD)} stroke={keyStroke(fingering.palmD)} strokeWidth="1.5" />
        <text x="90" y="55" textAnchor="middle" fontSize="7" fill="#ccc">D</text>

        {/* Bis key */}
        <ellipse cx="55" cy="85" rx="8" ry="6"
          fill={keyColor(fingering.bis)} stroke={keyStroke(fingering.bis)} strokeWidth="1.5" />
        <text x="55" y="88" textAnchor="middle" fontSize="6" fill="#ccc">Bis</text>

        {/* Left hand main keys */}
        <circle cx="35" cy="80" r="16"
          fill={keyColor(fingering.lh1)} stroke={keyStroke(fingering.lh1)} strokeWidth="2" />
        <text x="35" y="84" textAnchor="middle" fontSize="10" fill="#ccc">1</text>

        <circle cx="35" cy="120" r="16"
          fill={keyColor(fingering.lh2)} stroke={keyStroke(fingering.lh2)} strokeWidth="2" />
        <text x="35" y="124" textAnchor="middle" fontSize="10" fill="#ccc">2</text>

        <circle cx="35" cy="160" r="16"
          fill={keyColor(fingering.lh3)} stroke={keyStroke(fingering.lh3)} strokeWidth="2" />
        <text x="35" y="164" textAnchor="middle" fontSize="10" fill="#ccc">3</text>

        {/* G# key (left pinky) */}
        <rect x="65" y="145" width="28" height="10" rx="4"
          fill={keyColor(fingering.gSharp)} stroke={keyStroke(fingering.gSharp)} strokeWidth="1.5" />
        <text x="79" y="153" textAnchor="middle" fontSize="7" fill="#ccc">G#</text>

        {/* Divider line between hands */}
        <line x1="10" y1="190" x2="110" y2="190" stroke="#444" strokeWidth="1" strokeDasharray="4 3" />

        {/* Right hand main keys */}
        <circle cx="35" cy="210" r="16"
          fill={keyColor(fingering.rh1)} stroke={keyStroke(fingering.rh1)} strokeWidth="2" />
        <text x="35" y="214" textAnchor="middle" fontSize="10" fill="#ccc">1</text>

        <circle cx="35" cy="250" r="16"
          fill={keyColor(fingering.rh2)} stroke={keyStroke(fingering.rh2)} strokeWidth="2" />
        <text x="35" y="254" textAnchor="middle" fontSize="10" fill="#ccc">2</text>

        <circle cx="35" cy="290" r="16"
          fill={keyColor(fingering.rh3)} stroke={keyStroke(fingering.rh3)} strokeWidth="2" />
        <text x="35" y="294" textAnchor="middle" fontSize="10" fill="#ccc">3</text>

        {/* Side keys (right side) */}
        <rect x="65" y="205" width="28" height="10" rx="4"
          fill={keyColor(fingering.sideE)} stroke={keyStroke(fingering.sideE)} strokeWidth="1.5" />
        <text x="79" y="213" textAnchor="middle" fontSize="7" fill="#ccc">E</text>

        <rect x="65" y="221" width="28" height="10" rx="4"
          fill={keyColor(fingering.sideC)} stroke={keyStroke(fingering.sideC)} strokeWidth="1.5" />
        <text x="79" y="229" textAnchor="middle" fontSize="7" fill="#ccc">C</text>

        <rect x="65" y="237" width="28" height="10" rx="4"
          fill={keyColor(fingering.sideBb)} stroke={keyStroke(fingering.sideBb)} strokeWidth="1.5" />
        <text x="79" y="245" textAnchor="middle" fontSize="7" fill="#ccc">Bb</text>

        {/* Low keys (bottom) */}
        <rect x="65" y="270" width="28" height="10" rx="4"
          fill={keyColor(fingering.lowEb)} stroke={keyStroke(fingering.lowEb)} strokeWidth="1.5" />
        <text x="79" y="278" textAnchor="middle" fontSize="7" fill="#ccc">Eb</text>

        <rect x="65" y="286" width="28" height="10" rx="4"
          fill={keyColor(fingering.lowCSharp)} stroke={keyStroke(fingering.lowCSharp)} strokeWidth="1.5" />
        <text x="79" y="294" textAnchor="middle" fontSize="7" fill="#ccc">C#</text>

        <rect x="65" y="302" width="28" height="10" rx="4"
          fill={keyColor(fingering.lowC)} stroke={keyStroke(fingering.lowC)} strokeWidth="1.5" />
        <text x="79" y="310" textAnchor="middle" fontSize="7" fill="#ccc">C</text>

        <rect x="65" y="318" width="28" height="10" rx="4"
          fill={keyColor(fingering.lowB)} stroke={keyStroke(fingering.lowB)} strokeWidth="1.5" />
        <text x="79" y="326" textAnchor="middle" fontSize="7" fill="#ccc">B</text>

        <rect x="65" y="334" width="28" height="10" rx="4"
          fill={keyColor(fingering.lowBb)} stroke={keyStroke(fingering.lowBb)} strokeWidth="1.5" />
        <text x="79" y="342" textAnchor="middle" fontSize="7" fill="#ccc">Bb</text>

        {/* Hand labels */}
        <text x="5" y="125" fontSize="8" fill="#777" transform="rotate(-90, 5, 125)">{t('saxTutor.leftHand')}</text>
        <text x="5" y="260" fontSize="8" fill="#777" transform="rotate(-90, 5, 260)">{t('saxTutor.rightHand')}</text>
      </svg>
    </div>
  );
}

export default SaxFingeringDiagram;
